import {BizFormApiEndpoints, buildBizFormApiEndpoints} from "../../models/static/api-endpoints";
import BizFormEnvService from "../env-service";
import * as models from "./type-declarations";
import {ApiRequestDs} from "./type-declarations";
import {ApiMethods} from "../../models/constants/enums";
import {bizFormSharedApiExecutor} from "./shared-executor";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";

let cachedDefaultBizFormApiClient: BizFormApiClient | undefined;


/**
 * Cached BizForm API clients keyed by module (NCR vs app-wide operator bootstrap).
 */
export type BizFormApiTypes = "ncrService" | "operatorApp" | "bizForm";

/**
 * Builds a BizForm API client. When {@code baseUrl} is omitted or blank, uses
 *
 * @param type
 * @param baseUrl Optional API root URL (normalized with a trailing slash when building paths).
 */
export function createBizFormApiClient(type: BizFormApiTypes = 'bizForm', baseUrl?: string): BizFormApiClient {
    const explicit = baseUrl != null && String(baseUrl).trim() !== "";
    if (!explicit) {
        if (cachedDefaultBizFormApiClient) {
            return cachedDefaultBizFormApiClient;
        }
        const client = new BizFormApiClient(type, buildBizFormApiEndpoints(BizFormEnvService.ApiUrl));
        cachedDefaultBizFormApiClient = client;
        return client;
    }
    return new BizFormApiClient(type, buildBizFormApiEndpoints(String(baseUrl).trim()));
}

/**
 * BizForm HTTP API bound to a specific base URL. Shares axios interceptors with other instances.
 */
export class BizFormApiClient {
    private readonly endpoints: BizFormApiEndpoints;
    private readonly _baseUrl: string;
    private readonly type: BizFormApiTypes;

    /**
     * The base URL this client was configured with (derived from the create endpoint).
     */
    public get baseUrl(): string {
        return this._baseUrl;
    }

    constructor(type: BizFormApiTypes, endpoints: BizFormApiEndpoints) {
        this.endpoints = endpoints;
        this._baseUrl = endpoints.form.create.replace("Form/Create", "");
        this.type = type;
    }

    /**
     * Creates a new form in the server.
     * @param data    the data to create the form with.
     * @returns       the response of the api call.
     */
    async createForm(data: models.CreateFormRequest): models.CreateFormResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.form.create,
            data: data,
        });
    }

    /**
     * Updates the form in the server.
     * @param data    the data to update the form with.
     * @returns       the response of the api call.
     */
    async updateForm(data: models.UpdateFormRequest): models.UpdateFormResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.put,
            url: this.endpoints.form.update,
            data: data,
        });
    }

    /**
     * Fetches the form from the server.
     * @param data    the data to fetch the form with.
     * @param cancelToken    the token to cancel the request.
     * @returns       the response of the api call.
     */
    async getForm(data: models.GetFormRequest, cancelToken?: CancelToken): models.GetFormResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.get,
            url: this.endpoints.form.get(data.id),
            cancelToken: cancelToken,
        });
    }

    /**
     * Deletes the form from the server.
     * @param data
     */
    async deleteForm(data: models.DeleteFormRequest): models.DeleteFormResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.delete,
            url: this.endpoints.form.delete(data.id),
        });
    }

    /**
     * Lists forms for operator administration (title and per-status response counts).
     */
    async listForms(data: models.ListFormsRequest, cancelToken?: CancelToken): models.ListFormsResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.form.list,
            data,
            cancelToken,
        });
    }

    /**
     * Returns a lightweight list of forms by id for the current partner and external application.
     */
    async simpleListForms(data: models.SimpleListFormsRequest, cancelToken?: CancelToken): models.SimpleListFormsResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.form.simpleList,
            data,
            cancelToken,
        });
    }

    /**
     * Submits the form response to the server.
     * @param data    the data to submit the form response with.
     * @returns       the response of the api
     */
    async submitForm(data: models.SubmitFormRequest): models.SubmitFormResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.response.submit,
            data: data,
        });
    }

    /**
     * Submits a form response on behalf of an external user (no staff login).
     */
    async submitFormForExternalUser(data: models.SubmitFormForExternalUserRequest): models.SubmitFormForExternalUserResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.externalUserBased.submitForExternalUser,
            data,
        });
    }

    /**
     * Updates the form response in the server.
     * @param data    the data to update the form response with.
     * @returns       the response of the api call.
     */
    async updateFormResponse(data: models.UpdateFormResponseRequest): models.UpdateFormResponseResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.put,
            url: this.endpoints.response.update,
            data: data,
        });
    }

    /**
     * Fetches the form response from the server.
     *
     * @param data              the data to fetch the form response with.
     * @param cancelToken       the token to cancel the request.
     * @returns                 the response of the api call.
     */
    async getFormResponse(data: models.GetFormResponseRequest, cancelToken?: CancelToken): models.GetFormResponseResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.get,
            url: this.endpoints.response.get(data.id),
            cancelToken: cancelToken,
        });
    }

    /**
     * Lists form responses with optional filters (keyword, customer, operator).
     */
    async listFormResponses(data: models.ListFormResponsesRequest, cancelToken?: CancelToken): models.ListFormResponsesResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.response.list,
            data,
            cancelToken,
        });
    }

    /**
     * Submits a review for a form response (status, expiry, and comment).
     */
    async reviewFormResponse(data: models.ReviewFormResponseRequest): models.ReviewFormResponseResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.put,
            url: this.endpoints.response.review,
            data,
        });
    }

    /**
     * Deletes a form response by id.
     */
    async deleteFormResponse(id: string): models.DeleteFormResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.delete,
            url: this.endpoints.response.delete(id),
        });
    }

    /**
     * Creates a new question in the server.
     * @param data    the data to create the question with.
     * @returns       the response of the api call.
     */
    async createQuestion(data: models.CreateQuestionRequest): models.CreateQuestionResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.question.create,
            data: data,
        });
    }

    /**
     * Updates the question in the server.
     * @param data    the data to update the question with.
     * @returns       the response of the api call.
     */
    async updateQuestion(data: models.UpdateQuestionRequest): models.UpdateQuestionResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.put,
            url: this.endpoints.question.update,
            data: data,
        });
    }

    /**
     * Deletes the question in the server.
     * @param data    the data to delete the question with.
     * @returns       the response of the api call.
     */
    async deleteQuestion(data: models.DeleteQuestionRequest): models.DeleteQuestionResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.delete,
            url: this.endpoints.question.delete(data.questionId),
        });
    }

    /**
     * Changes the index of the question in the server.
     * @param data    the data to change the index of the question with.
     * @returns       the response of the api call.
     */
    async changeQuestionOrder(data: models.ChangeQuestionOrderRequest): models.ChangeQuestionOrderResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.question.changeIndex,
            data: data,
        });
    }

    /**
     * Deletes the options of a question.
     * @param data
     */
    async deleteQuestionOptions(data: models.DeleteQuestionOptionsRequest): models.DeleteQuestionOptionsResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.delete,
            url: this.endpoints.question.deleteOptions(data.questionId),
        });
    }

    /**
     * Creates a new option in the server.
     * @param data    the data to create the option with.
     * @returns       the response of the api call.
     */
    async createOption(data: models.CreateOptionRequest): models.CreateOptionResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.option.create,
            data: data,
        });
    }

    /**
     * Updates the option in the server.
     * @param data    the data to update the option with.
     * @returns       the response of the api call.
     */
    async updateOption(data: models.UpdateOptionRequest): models.UpdateOptionResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.put,
            url: this.endpoints.option.update,
            data: data,
        });
    }

    /**
     * Deletes the option in the server.
     * @param data    the data to delete the option with.
     * @returns       the response of the api call.
     */
    async deleteOption(data: models.DeleteOptionRequest): models.DeleteOptionResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.delete,
            url: this.endpoints.option.delete(data.optionId),
        });
    }

    /**
     * Changes the index of the option in the server.
     * @param data    the data to change the index of the option with.
     * @returns       the response of the api call.
     */
    async changeOptionOrder(data: models.ChangeOptionOrderRequest): models.ChangeOptionOrderResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.option.changeIndex,
            data: data,
        });
    }

    /**
     * Creates a new BizFile access token.
     *
     * @param token                 The token of the operator.
     * @param cancelToken           The cancel token that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    async createBizFileAccessToken(token?: string, cancelToken?: CancelToken): models.CreateBizFileAccessTokenResponse {
        if (!token) {
            return;
        }
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.get,
            url: this.endpoints.bizFile.createAccessToken,
            cancelToken: cancelToken,
            enforceAuth: false,
            showErrorToast: false,
            headers: {
                Authorization: token ?? "00000000-0000-0000-0000-000000000000",
            },
        });
    }

    /**
     * Uploads the initial file chunk to the server.
     *
     * @param request               The argument to upload the initial file chunk.
     * @param onUploadProgress      The callback that will be called when the upload progress changes.
     * @param cancelToken           The cancel token that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    async uploadFile(
        request: models.UploadFileRequest,
        onUploadProgress: ApiRequestDs['onUploadProgress'],
        cancelToken?: CancelToken,
    ): models.UploadFileResponse {
        if (this.type !== 'ncrService') {
            return (await Api.uploadFile(request, onUploadProgress)) as unknown as models.UploadFileResponse;
        }
        const formData = new FormData();
        formData.append('firstChunk', request.file, request.fileName);
        formData.append('totalFileSize', request.fileSize.toString());
        formData.append('accessLevelId', '3');
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.bizFile.uploadFile,
            data: formData,
            onUploadProgress: onUploadProgress,
            cancelToken: cancelToken,
        });
    }

    /**
     * Submits a file chunk to the server.
     *
     * @param request               The request to upload a file chunk.
     * @param onUploadProgress      The callback that will be called when the upload progress changes.
     * @param cancelToken           The cancel token that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    async uploadRemainingChunk(
        request: models.UploadRemainingChunkRequest,
        onUploadProgress: ApiRequestDs['onUploadProgress'],
        cancelToken?: CancelToken,
    ): models.UploadRemainingChunkResponse {
        if (this.type !== 'ncrService') {
            return (await Api.uploadRemainingChunk(request, onUploadProgress)) as unknown as models.UploadRemainingChunkResponse;
        }
        const formData = new FormData();
        formData.append('nextChunk', request.nextChunk, request.nextChunkFileName);
        formData.append('fileId', request.fileId);
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.bizFile.uploadRemainingChunk,
            data: formData,
            onUploadProgress,
            cancelToken: cancelToken,
        });
    }

    /**
     * Deletes a file from the server.
     * @param id                The id of the file to delete.
     */
    async deleteFile(id: any): models.DeleteFileResponse {
        if (this.type !== 'ncrService') {
            return (await Api.deleteFile(id)) as unknown as models.DeleteFileResponse;
        }
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.delete,
            url: this.endpoints.bizFile.deleteFile(id),
        });
    }

    /**
     * Creates a new BizFile storage account.
     *
     * @param cancelToken       The cancel token that will be used to cancel the request.
     * @returns                 The response from the server.
     */
    async createBizFileStorageAccount(cancelToken?: CancelToken): models.CreateBizFileStorageAccountResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.bizFile.createStorageAccount,
            cancelToken: cancelToken,
        });
    }

    /**
     * Get BizFile storage account.
     *
     * @param cancelToken       The cancel token that will be used to cancel the request.
     * @returns                 The response from the server.
     */
    async getBizFileStorageAccount(cancelToken?: CancelToken): models.GetBizFileStorageAccountResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.get,
            url: this.endpoints.bizFile.getStorageAccount,
            cancelToken: cancelToken,
            showErrorToast: false,
        });
    }

    // ------------------------------ External user ------------------------------

    /**
     * Gets an external user by internal reference for the current partner, or creates one if missing.
     */
    async getOrCreateExternalUser(data: models.GetOrCreateExternalUserRequest, cancelToken?: CancelToken): models.GetOrCreateExternalUserResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.externalUser.getOrCreate,
            data,
            cancelToken,
        });
    }

    // ------------------------------ External user based ------------------------------

    /**
     * Returns a public form definition for an external user (no staff login).
     */
    async getFormForExternalUser(data: models.GetFormForExternalUserRequest, cancelToken?: CancelToken): models.GetFormForExternalUserResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.externalUserBased.getForm,
            data,
            cancelToken,
        });
    }

    /**
     * Lists form responses for an external user (paginated, no staff login).
     */
    async listFormResponsesForExternalUser(
        data: models.ListFormResponsesForExternalUserRequest,
        cancelToken?: CancelToken,
    ): models.ListFormResponsesForExternalUserResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.externalUserBased.listFormResponses,
            data,
            cancelToken,
        });
    }

    /**
     * Returns a single form response for an external user (no staff login).
     */
    async getResponseForExternalUser(
        data: models.GetResponseForExternalUserRequest,
        cancelToken?: CancelToken,
    ): models.GetResponseForExternalUserResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.externalUserBased.getResponse,
            data,
            cancelToken,
        });
    }

    // ------------------------------ Partner settings ------------------------------

    /**
     * Returns partner-level BizForm settings for the current partner.
     */
    async getPartnerSettings(cancelToken?: CancelToken): models.GetPartnerSettingsResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.get,
            url: this.endpoints.setting.getPartnerSettings,
            cancelToken,
        });
    }

    /**
     * Updates a single partner setting by name.
     */
    async updatePartnerSetting(data: models.UpdatePartnerSettingRequest): models.UpdatePartnerSettingResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.setting.updatePartnerSetting,
            data,
        });
    }

    /**
     * Resets partner settings to their defaults.
     */
    async resetToDefaultPartnerSetting(cancelToken?: CancelToken): models.ResetToDefaultPartnerSettingResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.post,
            url: this.endpoints.setting.resetToDefaultPartnerSetting,
            cancelToken,
        });
    }

    // ------------------------------ Reporting tools ------------------------------

    /**
     * Returns column metadata for a named report.
     */
    async getReportingColumns(reportName: string, cancelToken?: CancelToken): models.ReportingGetColumnsResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.get,
            url: this.endpoints.reportingTools.getColumns(reportName),
            cancelToken,
        });
    }

    /**
     * Returns lookup key/value pairs for report filtering (static data from the database).
     */
    async getReportingLookup(
        apiKey: string,
        keyword?: string,
        cancelToken?: CancelToken,
    ): models.ReportingLookupResponse {
        return await bizFormSharedApiExecutor.execute({
            method: ApiMethods.get,
            url: this.endpoints.reportingTools.lookup(apiKey, keyword),
            cancelToken,
        });
    }
}
